// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useApp } from '../../store/AppContext.js';
import { buildEvent } from '../../lib/eventLog.js';
import { useEncryptAndPush } from '../../hooks/useEncryptAndPush.js';
import type { Budget, BudgetCreatedPayload, BudgetDeletedPayload } from '../../types.js';

interface BudgetManagerProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

const inputCls = 'w-full bg-bg border border-border text-text text-sm px-3 py-2 focus:border-accent focus:outline-none';
const labelCls = 'block text-xs text-text-muted mb-1';

export function BudgetManager({ worker, keyData }: BudgetManagerProps) {
  const { t } = useTranslation(['budgets', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();
  const encryptAndPush = useEncryptAndPush(worker, keyData);

  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newPeriod, setNewPeriod] = useState<Budget['periodType']>('monthly');
  const [newStart, setNewStart] = useState(new Date().toISOString().slice(0, 8) + '01');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const budgets = [...ledger.budgets.values()].sort((a, b) => a.name.localeCompare(b.name));

  async function handleCreate() {
    if (!newName.trim()) { setError(t('errors:nameRequired')); return; }
    setSaving(true);
    try {
      const numPeriods = newPeriod === 'monthly' ? 12 : newPeriod === 'quarterly' ? 4 : 1;
      const payload: BudgetCreatedPayload = {
        budgetId: uuidv4(),
        name: newName.trim(),
        description: newDesc.trim(),
        periodType: newPeriod,
        numPeriods,
        startDate: newStart,
        values: [],
      };
      await encryptAndPush(await buildEvent('BudgetCreated', payload, ledger.lastSequence + 1));
      setShowNew(false);
      setNewName('');
      setNewDesc('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors:createBudget'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(budget: Budget) {
    if (!confirm(t('errors:confirmDeleteBudget', { name: budget.name }))) return;
    const payload: BudgetDeletedPayload = { budgetId: budget.id };
    await encryptAndPush(await buildEvent('BudgetDeleted', payload, ledger.lastSequence + 1));
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-md font-semibold text-text">{t('common:nav.budgets')}</h2>
        <button
          onClick={() => setShowNew(true)}
          className="px-3 py-1.5 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          + {t('newBudget')}
        </button>
      </div>

      {budgets.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-text-subtle">
          <div className="text-center">
            <p className="text-base font-medium mb-1 text-text-muted">{t('noBudgetsYet')}</p>
            <p className="text-sm text-text-subtle">{t('manager.createPrompt')}</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {budgets.map((budget) => (
            <div key={budget.id} className="bg-bg-surface border border-border p-4 flex items-center justify-between hover:border-border-strong transition-colors">
              <div>
                <h3 className="font-medium text-text">{budget.name}</h3>
                {budget.description && <p className="text-sm text-text-muted">{budget.description}</p>}
                <p className="text-xs text-text-subtle mt-0.5 font-mono">
                  {budget.periodType} · {t('manager.periodCount', { count: budget.numPeriods })} · {t('manager.starts', { date: budget.startDate })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate(`/budgets/${budget.id}`)}
                  className="px-3 py-1.5 text-sm border border-border text-text-muted hover:bg-bg-elevated transition-colors"
                >
                  {t('common:actions.edit')}
                </button>
                <button
                  onClick={() => navigate(`/reports/budget?budgetId=${budget.id}`)}
                  className="px-3 py-1.5 text-sm border border-border text-text-muted hover:bg-bg-elevated transition-colors"
                >
                  {t('manager.report')}
                </button>
                <button
                  onClick={() => handleDelete(budget)}
                  className="px-3 py-1.5 text-sm text-negative hover:underline transition-colors"
                >
                  {t('common:actions.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-surface border border-border max-w-md w-full p-6">
            <h3 className="text-md font-semibold text-text mb-4">{t('newBudget')}</h3>
            {error && (
              <div className="border border-negative text-negative px-3 py-2 text-sm mb-4">{error}</div>
            )}
            <div className="space-y-4">
              <div>
                <label className={labelCls}>{t('manager.nameLabel')}</label>
                <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                  className={inputCls} placeholder={t('manager.namePlaceholder')} autoFocus />
              </div>
              <div>
                <label className={labelCls}>{t('manager.descriptionLabel')}</label>
                <input type="text" value={newDesc} onChange={(e) => setNewDesc(e.target.value)}
                  className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('manager.periodTypeLabel')}</label>
                  <select value={newPeriod} onChange={(e) => setNewPeriod(e.target.value as Budget['periodType'])}
                    className={inputCls}>
                    <option value="monthly">{t('manager.periodMonthly')}</option>
                    <option value="quarterly">{t('manager.periodQuarterly')}</option>
                    <option value="yearly">{t('manager.periodYearly')}</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t('manager.startDateLabel')}</label>
                  <input type="date" value={newStart} onChange={(e) => setNewStart(e.target.value)}
                    className={inputCls} />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowNew(false); setError(''); }}
                className="flex-1 py-2 border border-border text-sm text-text-muted hover:bg-bg-elevated transition-colors">
                {t('common:actions.cancel')}
              </button>
              <button onClick={handleCreate} disabled={saving}
                className="flex-1 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover disabled:opacity-50 transition-colors">
                {saving ? t('common:actions.creating') : t('createBudget')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
